import { ActionLink } from '@cian/ui-kit/link';
import { ArticleHeading3, Body2 } from '@cian/ui-kit/typography';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';

import style from './style.css';

export interface IBuildingDescriptionProps {
  title: string | null;
  text: string | null;
  /** Источники фотографий: в монолите выводятся внутри секции описания */
  photoSources: string[];
}

/** Абзацы и переносы строк как фильтр `linebreaks` в Django */
const toParagraphs = (text: string): string[][] =>
  text
    .replace(/\r\n|\r/g, '\n')
    .split(/\n{2,}/)
    .filter(paragraph => paragraph.trim())
    .map(paragraph => paragraph.split('\n'));

export const BuildingDescription = ({ title, text, photoSources }: IBuildingDescriptionProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isOpened, setIsOpened] = useState(false);

  // Как в монолите: «Показать еще» появляется после загрузки скрипта, если текст не влезает
  useLayoutEffect(() => {
    const node = textRef.current;

    if (node) {
      setIsOverflowing(node.scrollHeight > node.clientHeight);
    }
  }, [text]);

  if (!text) {
    return null;
  }

  return (
    <section className={style['container']} data-name="BuildingDescription">
      {title && <ArticleHeading3 as="h2">{title}</ArticleHeading3>}
      <div
        ref={textRef}
        className={isOpened ? style['text'] : `${style['text']} ${style['text--limited']}`}
        data-name="BuildingDescriptionText"
      >
        {toParagraphs(text).map((lines, paragraphIndex) => (
          <Body2 key={paragraphIndex} as="p">
            {lines.map((line, lineIndex) => (
              <Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {line}
              </Fragment>
            ))}
          </Body2>
        ))}
      </div>
      {(isOverflowing || isOpened) && (
        <ActionLink onClick={() => setIsOpened(opened => !opened)}>{isOpened ? 'Скрыть' : 'Показать еще'}</ActionLink>
      )}
      {photoSources.length > 0 && (
        <div className={style['sources']}>
          <Body2 color="text-secondary-default">Источники фотографий: {photoSources.join(', ')}</Body2>
        </div>
      )}
    </section>
  );
};
